import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {
  GridRowModes,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { dataGridPremiumColumns } from '../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  InputUI,
  AutoCompleteSelectUI,
  AddButtonUi,
  DatagridPremiumUi,
} from '../Interface';

function EditToolbar(props) {
  const { disabled, push, defaultField, autoCompleteData } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    defaultField.id = uuidv4();
    defaultField.costAndRevenueTypeId = values.costAndRevenueTypeId;
    defaultField.costCenterCode = values.costCenterCode;
    defaultField.projectId = values.projectId;

    const crType = autoCompleteData?.costAndRevenue?.find((item) => item.id === values.costAndRevenueTypeId);

    defaultField.relatedMasterDataUrl = crType?.relatedMasterDataUrl ?? null;

    await push(defaultField);

    const fieldsToValidate = [
      `otherExpenseLine.${values.otherExpenseLine.length ?? 0}.costAndRevenueTypeId`,
      `otherExpenseLine.${values.otherExpenseLine.length ?? 0}.relatedId`,
      `otherExpenseLine.${values.otherExpenseLine.length ?? 0}.name`,
      `otherExpenseLine.${values.otherExpenseLine.length ?? 0}.amount`,
      `otherExpenseLine.${values.otherExpenseLine.length ?? 0}.costCenterCode`,
      `otherExpenseLine.${values.otherExpenseLine.length ?? 0}.projectId`,
      `otherExpenseLine.${values.otherExpenseLine.length ?? 0}.companyId`,
      `otherExpenseLine.${values.otherExpenseLine.length ?? 0}.reference`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const OtherExpenseLine = ({ disabled, defaultField, autocompleteOptions, autoCompleteData }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [{ field: 'name', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const getValueIndex = (values, id) => {
    return values.otherExpenseLine.findIndex((rc) => rc.id === id);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'costAndRevenueTypeId',
        headerName: 'Költség fajta',
        editable: true,
        type: 'string',
        sortable: false,
        renderCell: (data) => {
          const finded = autocompleteOptions?.costAndRevenue.find(
            (item) => item.value === data.row?.costAndRevenueTypeId
          );
          return finded?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const finded = autocompleteOptions.costAndRevenue.find(
            (item) => item.value === data.row.costAndRevenueTypeId
          );

          return (
            <Field
              name={`otherExpenseLine.${valueIndex}.costAndRevenueTypeId`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.otherExpenseLine?.[valueIndex]?.costAndRevenueTypeId} />}
              selectedValue={values.otherExpenseLine?.[valueIndex]?.costAndRevenueTypeId}
              selectedLabelValue={finded?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`otherExpenseLine.${valueIndex}.costAndRevenueTypeId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal.value ?? null,
                });
              }}
              dataset={autocompleteOptions.costAndRevenue}
              onBlur={async () => await validateField(`otherExpenseLine.${valueIndex}.costAndRevenueTypeId`)}
            />
          );
        },
      },
      {
        field: 'relatedId',
        headerName: 'Dolgozó',
        editable: true,
        sortable: false,
        type: 'string',
        renderCell: (data) => {
          const finded = autocompleteOptions?.resourceOne.find((item) => item.value === data.row?.relatedId);
          return finded?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const finded = autocompleteOptions.resourceOne.find((item) => item.value === data.row.relatedId);

          return (
            <Field
              name={`otherExpenseLine.${valueIndex}.relatedId`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.otherExpenseLine?.[valueIndex]?.relatedId} />}
              selectedValue={values.otherExpenseLine?.[valueIndex]?.relatedId}
              selectedLabelValue={finded?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`otherExpenseLine.${valueIndex}.relatedId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal.value ?? null,
                });
              }}
              dataset={autocompleteOptions.resourceOne}
              onBlur={async () => await validateField(`otherExpenseLine.${valueIndex}.relatedId`)}
            />
          );
        },
      },
      {
        field: 'name',
        headerName: 'Megnevezés',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`otherExpenseLine.${valueIndex}.name`}
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.otherExpenseLine?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`otherExpenseLine.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`otherExpenseLine.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'amount',
        headerName: 'Összeg',
        type: 'number',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const value = Number(data.value);
          return value.toLocaleString('hu-HU');
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`otherExpenseLine.${valueIndex}.amount`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.otherExpenseLine?.[valueIndex]?.amount} />}
              onChange={(e) => {
                setFieldValue(`otherExpenseLine.${valueIndex}.amount`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`otherExpenseLine.${valueIndex}.amount`)}
            />
          );
        },
      },
      {
        field: 'costCenterCode',
        headerName: 'Költséghely',
        editable: true,
        sortable: false,
        type: 'string',
        renderCell: (data) => {
          const finded = autocompleteOptions?.costCenter.find((item) => item.value === data.row?.costCenterCode);
          return finded?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const finded = autocompleteOptions.costCenter.find((item) => item.value === data.row.costCenterCode);

          return (
            <Field
              name={`otherExpenseLine.${valueIndex}.costCenterCode`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.otherExpenseLine?.[valueIndex]?.costCenterCode} />}
              selectedValue={values.otherExpenseLine?.[valueIndex]?.costCenterCode}
              selectedLabelValue={finded?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`otherExpenseLine.${valueIndex}.costCenterCode`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              dataset={autocompleteOptions.costCenter}
              onBlur={async () => await validateField(`otherExpenseLine.${valueIndex}.costCenterCode`)}
            />
          );
        },
      },
      {
        field: 'projectId',
        headerName: 'Projekt',
        editable: true,
        sortable: false,
        type: 'string',
        renderCell: (data) => {
          const finded = autocompleteOptions?.project.find((item) => item.value === data.row?.projectId);
          return finded?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const finded = autocompleteOptions.project.find((item) => item.value === data.row.projectId);

          return (
            <Field
              name={`otherExpenseLine.${valueIndex}.projectId`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.otherExpenseLine?.[valueIndex]?.projectId} />}
              selectedValue={values.otherExpenseLine?.[valueIndex]?.projectId}
              selectedLabelValue={finded?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`otherExpenseLine.${valueIndex}.projectId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              dataset={autocompleteOptions.project}
              onBlur={async () => await validateField(`otherExpenseLine.${valueIndex}.projectId`)}
            />
          );
        },
      },
      {
        field: 'companyId',
        headerName: 'Üzleti partner',
        editable: true,
        sortable: false,
        type: 'string',
        renderCell: (data) => {
          const finded = autocompleteOptions?.company.find((item) => item.value === data.row?.companyId);
          return finded?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const finded = autocompleteOptions.company.find((item) => item.value === data.row.companyId);

          return (
            <Field
              name={`otherExpenseLine.${valueIndex}.companyId`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.otherExpenseLine?.[valueIndex]?.companyId} />}
              selectedValue={values.otherExpenseLine?.[valueIndex]?.companyId}
              selectedLabelValue={finded?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`otherExpenseLine.${valueIndex}.companyId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal?.value ?? null,
                });
              }}
              dataset={autocompleteOptions.company}
              onBlur={async () => await validateField(`otherExpenseLine.${valueIndex}.companyId`)}
            />
          );
        },
      },
      {
        field: 'reference',
        headerName: 'Hivatkozási szám',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`otherExpenseLine.${valueIndex}.reference`}
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.otherExpenseLine?.[valueIndex]?.reference} />}
              onChange={(e) => {
                setFieldValue(`otherExpenseLine.${valueIndex}.reference`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`otherExpenseLine.${valueIndex}.reference`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        flex: 0.25,
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="otherExpenseLine">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.otherExpenseLine}
                height={530}
                datagridInitialState={initialState}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.otherExpenseLine ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    autoCompleteData,
                    defaultField,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                headerFilters={false}
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default OtherExpenseLine;
